/**
 * Document Picture-in-Picture da chamada (estilo Google Meet).
 * Janela flutuante com vídeo + mute / câmera / tela / encerrar.
 */

import { getProcessorOutputCanvas, startProcessorPreviewCopy } from "./videoBackground";
import { participantLabelFromIdentity } from "./videoParticipantLabel";

const DEFAULT_SIZE = { width: 360, height: 560 };

export function isDocumentPipSupported() {
  try {
    return typeof window !== "undefined" && "documentPictureInPicture" in window;
  } catch {
    return false;
  }
}

export function isDocumentPipOpen() {
  try {
    return Boolean(window.documentPictureInPicture?.window);
  } catch {
    return false;
  }
}

function labelFromIdentity(identity, fallback = "Participante", knownName) {
  return participantLabelFromIdentity(identity, knownName, fallback);
}

function isScreenShareTrack(track) {
  if (!track) return false;
  const name = String(track.name || "").toLowerCase();
  return (
    track.kind === "video" &&
    (name.includes("screen") || name.includes("presentation") || track.isScreenShare === true)
  );
}

const pipPreviewStops = new WeakMap();

/** Canvas de preview ligado a este <video>, sem varrer o palco inteiro (senão apaga a câmera local). */
export function pipPreviewCanvasFor(videoEl) {
  if (!videoEl?.parentElement) return null;
  const stored = pipPreviewStops.get(videoEl)?.canvas;
  if (stored?.isConnected) return stored;
  return (
    Array.from(videoEl.parentElement.children).find(
      (el) => el.tagName === "CANVAS" && el.getAttribute("data-video-bg-preview") === "1"
    ) || null
  );
}

export function stopPipPreviewCopy(videoEl) {
  if (!videoEl) return;
  const entry = pipPreviewStops.get(videoEl);
  if (entry?.stop) {
    try {
      entry.stop();
    } catch {
      /* ignore */
    }
  }
  pipPreviewStops.delete(videoEl);
  const canvas = entry?.canvas || pipPreviewCanvasFor(videoEl);
  if (canvas && canvas.parentElement) canvas.remove();
  videoEl.style.opacity = "";
}

function stopPipMedia(pipDoc) {
  if (!pipDoc) return;
  pipDoc.querySelectorAll("video").forEach((el) => {
    stopPipPreviewCopy(el);
    try {
      el.srcObject = null;
    } catch {
      /* ignore */
    }
  });
  pipDoc.querySelectorAll("canvas[data-video-bg-preview]").forEach((el) => el.remove());
}

export function collectFeaturedVideoSources(room, nameForIdentity, { preferredSid = null, preferredIdentity = null } = {}) {
  if (!room) return { featured: null, featuredLabel: "", local: null, featuredCamOff: false };
  const remotes = [];
  room.participants?.forEach?.((p) => {
    let addedEnabled = false;
    p.videoTracks?.forEach?.((pub) => {
      if (!pub.isSubscribed || !pub.track) return;
      const screen = isScreenShareTrack(pub.track);
      if (!screen && pub.track.isEnabled === false) return;
      remotes.push({
        track: pub.track,
        identity: p.identity,
        sid: p.sid,
        screen,
        camOff: false,
      });
      addedEnabled = true;
    });
    if (!addedEnabled) {
      remotes.push({
        track: null,
        identity: p.identity,
        sid: p.sid,
        screen: false,
        camOff: true,
      });
    }
  });

  const preferredLive =
    remotes.find(
      (r) =>
        !r.camOff &&
        ((preferredSid && r.sid === preferredSid) ||
          (preferredIdentity && r.identity === preferredIdentity))
    ) || null;
  const screen = remotes.find((r) => r.screen);
  const cam = remotes.find((r) => !r.screen && !r.camOff);
  const camOffRemote = remotes.find((r) => r.camOff);
  // Tela compartilhada manda; senão falante/pin com vídeo; senão qualquer cam; senão cam-off.
  const featured = screen || preferredLive || cam || camOffRemote || null;

  let local = null;
  room.localParticipant?.videoTracks?.forEach?.((pub) => {
    if (!pub.track || isScreenShareTrack(pub.track)) return;
    if (pub.track.isEnabled === false) return;
    local = pub.track;
  });

  const known =
    featured && typeof nameForIdentity === "function" ? nameForIdentity(featured.identity) : null;
  let featuredLabel = "";
  if (featured) {
    featuredLabel = labelFromIdentity(
      featured.identity,
      featured.screen ? "Tela compartilhada" : "Participante",
      known
    );
    if (featured.camOff) featuredLabel = `${featuredLabel} · câmera desligada`;
  }

  return {
    featured: featured?.track || null,
    featuredLabel,
    featuredCamOff: Boolean(featured?.camOff),
    local,
  };
}

function attachTwilioTrackToVideo(track, videoEl, { mirror = false, contain = false } = {}) {
  if (!track || !videoEl) return;
  try {
    const mediaStreamTrack = track.processedTrack || track.mediaStreamTrack;
    if (mediaStreamTrack) {
      videoEl.srcObject = new MediaStream([mediaStreamTrack]);
    } else {
      // Fallback: Twilio attach()
      const el = track.attach();
      if (el?.srcObject) videoEl.srcObject = el.srcObject;
      try {
        track.detach(el);
        el.remove?.();
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
  videoEl.muted = true;
  videoEl.playsInline = true;
  videoEl.autoplay = true;
  videoEl.style.objectFit = contain || isScreenShareTrack(track) ? "contain" : "cover";
  videoEl.style.transform = mirror && !contain && !isScreenShareTrack(track) ? "scaleX(-1)" : "";
  videoEl.play?.().catch?.(() => {});
}

/**
 * Local + fundo: copia o canvas do processor para o documento do PiP.
 * `processedTrack` (captureStream) some na janela flutuante; remotes seguem no WebRTC.
 */
function attachTrackToPipSurface(track, videoEl, pipDoc, { mirror = false, contain = false } = {}) {
  if (!track || !videoEl) return;
  stopPipPreviewCopy(videoEl);
  attachTwilioTrackToVideo(track, videoEl, { mirror, contain });
  const srcCanvas = getProcessorOutputCanvas(track);
  const parent = videoEl.parentElement;
  if (!srcCanvas || !parent || !pipDoc) return;
  const overlay = pipDoc.createElement("canvas");
  overlay.setAttribute("data-video-bg-preview", "1");
  overlay.style.position = "absolute";
  overlay.style.inset = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.objectFit = contain || isScreenShareTrack(track) ? "contain" : "cover";
  overlay.style.transform = mirror && !contain && !isScreenShareTrack(track) ? "scaleX(-1)" : "";
  overlay.style.display = "block";
  overlay.style.zIndex = "1";
  overlay.style.pointerEvents = "none";
  const stop = startProcessorPreviewCopy(track, overlay, {
    rafWindow: pipDoc.defaultView,
    onFrame: () => {
      if (overlay.isConnected) videoEl.style.opacity = "0";
    },
  });
  if (!stop) return;
  parent.appendChild(overlay);
  pipPreviewStops.set(videoEl, { stop, canvas: overlay });
}

export function buildPipStyles() {
  return `
    :root { color-scheme: dark; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0; height: 100%;
      background: #202124;
      color: #e8eaed;
      font-family: "Google Sans", Roboto, system-ui, -apple-system, sans-serif;
      overflow: hidden;
      user-select: none;
    }
    .shell {
      display: flex; flex-direction: column;
      height: 100%; min-height: 100%;
      background: #202124;
    }
    .stage {
      position: relative;
      flex: 1;
      min-height: 0;
      background: #000;
      margin: 8px 8px 0;
      border-radius: 16px;
      overflow: hidden;
    }
    .stage > video.main,
    .stage > canvas[data-video-bg-preview] {
      width: 100%; height: 100%;
      object-fit: contain;
      object-position: center center;
      background: #000;
      display: block;
    }
    .stage .empty {
      position: absolute; inset: 0;
      place-items: center;
      color: #9aa0a6; font-size: 13px; text-align: center; padding: 16px;
      z-index: 2;
      pointer-events: none;
      display: none;
    }
    .stage .empty:not([hidden]) {
      display: grid;
    }
    .stage .empty[hidden] {
      display: none !important;
    }
    .name-pill {
      position: absolute; left: 10px; bottom: 10px;
      max-width: calc(100% - 20px);
      padding: 6px 12px;
      border-radius: 999px;
      background: rgba(32,33,36,0.78);
      font-size: 12px; font-weight: 500;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      z-index: 2;
    }
    .local-pip {
      position: absolute; right: 10px; bottom: 10px;
      width: 96px; height: 72px;
      border-radius: 10px; overflow: hidden;
      border: 1px solid rgba(255,255,255,0.14);
      background: #3c4043;
      box-shadow: 0 4px 12px rgba(0,0,0,0.35);
      z-index: 2;
    }
    .local-pip video,
    .local-pip canvas[data-video-bg-preview] {
      width: 100%; height: 100%; object-fit: cover; display: block;
    }
    .bar {
      display: flex; justify-content: center; align-items: center;
      gap: 10px;
      padding: 12px 10px 14px;
      flex-shrink: 0;
    }
    .btn {
      width: 44px; height: 44px; border-radius: 50%;
      border: none; cursor: pointer;
      background: #3c4043; color: #fff;
      display: grid; place-items: center;
      transition: background .15s ease;
    }
    .btn:hover { background: #5f6368; }
    .btn.off { background: #ea4335; }
    .btn.end { background: #ea4335; width: 52px; height: 52px; }
    .btn.end:hover { background: #f28b82; }
    .btn svg { width: 22px; height: 22px; fill: currentColor; }
    .btn.end svg { width: 24px; height: 24px; }
  `;
}

const ICONS = {
  mic: `<svg viewBox="0 0 24 24"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>`,
  micOff: `<svg viewBox="0 0 24 24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>`,
  cam: `<svg viewBox="0 0 24 24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>`,
  camOff: `<svg viewBox="0 0 24 24"><path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2 2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/></svg>`,
  screen: `<svg viewBox="0 0 24 24"><path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/></svg>`,
  hangup: `<svg viewBox="0 0 24 24"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 0 0-2.67-1.85.996.996 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>`,
};

/**
 * @param {object} opts
 * @param {() => any} opts.getRoom
 * @param {() => { muted: boolean, camOff: boolean, sharingScreen: boolean, displayName: string, roomLabel: string }} opts.getState
 * @param {{ toggleMute: Function, toggleCam: Function, toggleScreenShare: Function, leave: Function, focusOpener?: Function }} opts.actions
 * @param {{ brandName?: string, unsupportedMessage?: string }} [opts.copy]
 */
export async function openVideoCallDocumentPip(opts = {}) {
  if (!isDocumentPipSupported()) {
    const err = new Error(opts.copy?.unsupportedMessage || "Picture-in-Picture não suportado neste navegador.");
    err.code = "video.pip_unsupported";
    throw err;
  }

  // Já aberto
  if (window.documentPictureInPicture.window) {
    try {
      window.documentPictureInPicture.window.focus?.();
    } catch {
      /* ignore */
    }
    return window.documentPictureInPicture.window;
  }

  const pipWindow = await window.documentPictureInPicture.requestWindow({
    width: DEFAULT_SIZE.width,
    height: DEFAULT_SIZE.height,
    preferInitialWindowPlacement: true,
  });

  const pipDoc = pipWindow.document;
  pipDoc.head.innerHTML = "";
  const style = pipDoc.createElement("style");
  style.textContent = buildPipStyles();
  pipDoc.head.appendChild(style);
  pipDoc.title = opts.copy?.brandName || "Vídeo XPro";

  pipDoc.body.innerHTML = `
    <div class="shell">
      <div class="stage">
        <video class="main" playsinline autoplay muted></video>
        <div class="empty" hidden>Aguardando vídeo…</div>
        <div class="name-pill"></div>
        <div class="local-pip" hidden><video playsinline autoplay muted></video></div>
      </div>
      <div class="bar">
        <button type="button" class="btn" data-action="mute" title="Microfone">${ICONS.mic}</button>
        <button type="button" class="btn" data-action="cam" title="Câmera">${ICONS.cam}</button>
        <button type="button" class="btn" data-action="screen" title="Compartilhar tela">${ICONS.screen}</button>
        <button type="button" class="btn end" data-action="leave" title="Sair da chamada">${ICONS.hangup}</button>
      </div>
    </div>
  `;

  const mainVideo = pipDoc.querySelector("video.main");
  const emptyEl = pipDoc.querySelector(".empty");
  const namePill = pipDoc.querySelector(".name-pill");
  const localWrap = pipDoc.querySelector(".local-pip");
  const localVideo = pipDoc.querySelector(".local-pip video");
  const muteBtn = pipDoc.querySelector('[data-action="mute"]');
  const camBtn = pipDoc.querySelector('[data-action="cam"]');
  const screenBtn = pipDoc.querySelector('[data-action="screen"]');
  const leaveBtn = pipDoc.querySelector('[data-action="leave"]');

  const syncControls = () => {
    const state = opts.getState?.() || {};
    if (muteBtn) {
      muteBtn.classList.toggle("off", Boolean(state.muted));
      muteBtn.innerHTML = state.muted ? ICONS.micOff : ICONS.mic;
      muteBtn.title = state.muted ? "Ativar microfone" : "Desativar microfone";
    }
    if (camBtn) {
      const required = Boolean(state.requireCamera);
      camBtn.hidden = required;
      camBtn.disabled = required;
      camBtn.classList.toggle("off", Boolean(state.camOff));
      camBtn.innerHTML = state.camOff ? ICONS.camOff : ICONS.cam;
      camBtn.title = required
        ? "Câmera obrigatória nesta chamada"
        : state.camOff
          ? "Ligar câmera"
          : "Desligar câmera";
    }
    if (screenBtn) {
      screenBtn.classList.toggle("off", Boolean(state.sharingScreen));
      screenBtn.title = state.sharingScreen
        ? "Parar de compartilhar a tela"
        : "Compartilhar tela";
    }
  };

  const bindPipVideo = (el, track, { mirror = false, contain = false } = {}) => {
    if (!el || !track) return;
    const id = track.sid || track.mediaStreamTrack?.id || "";
    const bg = getProcessorOutputCanvas(track) ? "1" : "0";
    const key = `${id}|${bg}|${mirror ? "1" : "0"}`;
    const isLocal = Boolean(track.processor || track._outputFrame);
    const overlayMissing = isLocal && bg === "1" && !pipPreviewCanvasFor(el);
    if (el.dataset.surfaceKey === key && !overlayMissing) return;
    if (isLocal) {
      attachTrackToPipSurface(track, el, pipDoc, { mirror, contain });
    } else {
      stopPipPreviewCopy(el);
      attachTwilioTrackToVideo(track, el, { mirror, contain });
    }
    el.dataset.surfaceKey = key;
  };

  const clearPipVideo = (el) => {
    if (!el) return;
    stopPipPreviewCopy(el);
    try {
      el.srcObject = null;
    } catch {
      /* ignore */
    }
    el.dataset.surfaceKey = "";
  };

  const syncVideo = () => {
    const room = opts.getRoom?.();
    const state = opts.getState?.() || {};
    const nameForIdentity = state.nameForIdentity;
    const preferredSid = state.featuredSid || null;
    const preferredIdentity = state.featuredIdentity || null;
    const mirrorLocal = state.mirrorLocal !== false;
    const { featured, featuredLabel, featuredCamOff, local } = collectFeaturedVideoSources(
      room,
      nameForIdentity,
      { preferredSid, preferredIdentity }
    );

    if (featured && mainVideo) {
      bindPipVideo(mainVideo, featured, {
        contain: true,
        mirror: featured === local ? mirrorLocal : false,
      });
      if (emptyEl) emptyEl.hidden = true;
      if (namePill) {
        namePill.hidden = false;
        namePill.textContent = featuredLabel || state.roomLabel || "Chamada";
      }
    } else if (featuredCamOff) {
      if (local && mainVideo) {
        bindPipVideo(mainVideo, local, { mirror: mirrorLocal });
        if (emptyEl) emptyEl.hidden = true;
      } else if (mainVideo) {
        clearPipVideo(mainVideo);
        if (emptyEl) {
          emptyEl.hidden = false;
          emptyEl.textContent = featuredLabel || "Câmera desligada";
        }
      }
      if (namePill) {
        namePill.hidden = false;
        namePill.textContent = featuredLabel || "Câmera desligada";
      }
    } else if (local && mainVideo) {
      bindPipVideo(mainVideo, local, { mirror: mirrorLocal });
      if (emptyEl) emptyEl.hidden = true;
      if (namePill) {
        namePill.hidden = false;
        namePill.textContent = state.displayName || "Você";
      }
    } else {
      clearPipVideo(mainVideo);
      if (emptyEl) {
        emptyEl.hidden = false;
        emptyEl.textContent = "Aguardando participantes…";
      }
      if (namePill) namePill.hidden = true;
    }

    if (local && featured && featured !== local && localVideo && localWrap) {
      localWrap.hidden = false;
      bindPipVideo(localVideo, local, { mirror: mirrorLocal });
    } else if (localWrap) {
      localWrap.hidden = true;
      clearPipVideo(localVideo);
    }
  };

  muteBtn?.addEventListener("click", () => {
    opts.actions?.toggleMute?.();
    setTimeout(syncControls, 0);
  });
  camBtn?.addEventListener("click", () => {
    opts.actions?.toggleCam?.();
    setTimeout(() => {
      syncControls();
      syncVideo();
    }, 0);
  });
  screenBtn?.addEventListener("click", () => {
    opts.actions?.toggleScreenShare?.();
    setTimeout(() => {
      syncControls();
      syncVideo();
    }, 50);
  });
  leaveBtn?.addEventListener("click", () => {
    opts.actions?.leave?.();
    try {
      pipWindow.close();
    } catch {
      /* ignore */
    }
  });

  syncControls();
  syncVideo();

  const refreshTimer = pipWindow.setInterval(() => {
    syncControls();
    syncVideo();
  }, 1000);

  const onPageHide = () => {
    try {
      pipWindow.clearInterval(refreshTimer);
    } catch {
      /* ignore */
    }
    stopPipMedia(pipDoc);
  };
  pipWindow.addEventListener("pagehide", onPageHide);
  pipWindow.addEventListener("unload", onPageHide);

  return pipWindow;
}

export async function closeVideoCallDocumentPip() {
  try {
    const w = window.documentPictureInPicture?.window;
    if (w && !w.closed) w.close();
  } catch {
    /* ignore */
  }
}
